import {useEffect, useState} from 'react'
import {useLocation} from 'react-router-dom'
import {mdiEye, mdiDownloadCircleOutline, mdiDownload} from '@mdi/js'
import {Snackbar, Typography} from '@mui/material'
import Icon from '@mdi/react'
import PrimaryButton from 'src/components/elements/Buttons/PrimaryButton/PrimaryButton'
import SnackbarAlertCustom from 'src/components/elements/SnackbarAlertCustom/SnackbarAlertCustom'
import TableCustom, {PaginationType, TableColumn} from 'src/components/elements/TableCustom/TableCustom'
import LayoutContainer from 'src/components/layouts/LayoutContainer/LayoutContainer'
import TabsLayout from 'src/components/layouts/TabsLayout/TabsLayout'
import {PATHS, STATUS} from 'src/constants/constants'
import {useAppSelector} from 'src/hooks'
import apiClient from 'src/services/apiClient'
import Filters, {FilterItem} from 'src/components/templates/Filters/Filters'
import {TherapyDiaryStyled} from './TherapyDiaryStyled'
import FileViewerModal from 'src/components/elements/FileViewerModal/FileViewerModal'
import SecondaryAssumptionsDiaryPanel from './SecondaryAssumptionsDiaryPanel'

const assumptionsStateOptions = [
  {
    value: 'COMPLETED',
    label: 'Effettuata'
  },
  {
    value: 'PLANNED',
    label: 'Pianificata'
  },
  {
    value: 'TO_BE_PLANNED',
    label: 'In pianificazione'
  },
  {
    value: 'CANCELLED',
    label: 'Non effettuata'
  }
]

const TherapyDiary = () => {
  // SecondaryAssumptionResponse's "Vedi tutte le assunzioni" link lands here wanting the
  // "Terapie Accessorie" tab open directly, rather than making the patient re-select it.
  const location = useLocation<{tab?: 'primary' | 'secondary'; secondaryTherapyId?: string} | undefined>()
  const {columns = [], tableTitle = '', showExport, showTherapiesFilter, diaryType} = useAppSelector(state => state.pspConfigReducer.data.therapyDiaryConfigurations ?? {})
  const {serviceConf, _id: patientId, firstName: userFirstName, lastName: userLastName} = useAppSelector(state => state.userReducer.data)

  const [data, setData] = useState<Record<string, unknown>[]>([])
  const [selectedTherapy, setSelectedTherapy] = useState<string>()
  const [primaryTherapyOptions, setPrimaryTherapyOptions] = useState<Record<string, unknown>[]>([])
  const [secondaryTherapyOptions, setSecondaryTherapyOptions] = useState<Record<string, unknown>[]>([])
  // psp-backend/diary's own PRIMARY/SECONDARY split — which collection `selectedTherapy` refers
  // to. undefined = "Tipo: Tutte", the default: both collections merged and sorted by date. Only
  // meaningful for the DIARY_DRUGS/DIARY_INJECTIONS path below; DIARY_MORISKY is a separate
  // self-report questionnaire collection this doesn't touch at all.
  const [therapyType, setTherapyType] = useState<'PRIMARY' | 'SECONDARY' | undefined>(undefined)
  const [dateFrom, setDateFrom] = useState<string>()
  const [dateTo, setDateTo] = useState<string>()
  const [count, setCount] = useState(0)
  const [status, setStatus] = useState<any>(undefined)
  const [statusCount, setStatusCount] = useState<string>()
  const [pagination, setPagination] = useState<PaginationType>({
    page: 0,
    rowsPerPage: 10,
    order: ''
  })

  const fetchData = async () => {
    if (diaryType === 'DIARY_MORISKY') {
      const fetchedData = await apiClient.getDiaryMorisky({...pagination, therapy: selectedTherapy}, setStatus) ?? []
      setData(fetchedData)
      const fetchedDataCount = await apiClient.getDiaryMoriskyCount({therapy: selectedTherapy}, setStatusCount) ?? []
      setCount(fetchedDataCount)
    } else {
      // `therapy` only means anything alongside an explicit therapyType — omitted (Tipo: Tutte)
      // merges both sources, and a bare plan id can't say which collection it belongs to.
      const therapy = therapyType ? selectedTherapy : undefined
      const fetchedData = await apiClient.getPatientDiary({...pagination, therapy, therapyType, dateFrom, dateTo}, setStatus) ?? []
      const parsedData = fetchedData.map((assumption: any) => {
        return {
          ...assumption,
          state: assumptionsStateOptions.find((option) => option.value === assumption.state)?.label
        }
      })
      setData(parsedData)
      const fetchedDataCount = await apiClient.getPatientDiaryCount({therapy, therapyType, dateFrom, dateTo}, setStatusCount) ?? []
      setCount(fetchedDataCount)
    }
  }

  const fetchTherapies = async () => {
    const [fetchedPrimary, fetchedSecondary] = await Promise.all([
      apiClient.getTherapies(setStatus).catch(() => []) ?? [],
      apiClient.getSecondaryTherapies(() => {}).catch(() => []) ?? []
    ])
    setPrimaryTherapyOptions(fetchedPrimary)
    setSecondaryTherapyOptions(fetchedSecondary)
    // Default stays PRIMARY + its first therapy, unchanged from before this filter existed —
    // nothing changes for a PSP with no accessory therapies to switch to.
    if (fetchedPrimary.length) {
      setSelectedTherapy(fetchedPrimary[0]._id)
    }
  }

  // Same "Filtra" button + popover convention as everywhere else in the legacy stack (see
  // buildFiltersConfig below for the field definitions). Filters only reports fields the user
  // actually set (an empty/"Tutte" select is stripped, not sent as ''), so a missing property
  // here means "back to the default" for it.
  const handleFiltersSubmit = (results: {property: string; value: any}[]) => {
    const nextType = results.find((r) => r.property === 'therapyType')?.value as 'PRIMARY' | 'SECONDARY' | undefined
    const nextTherapy = results.find((r) => r.property === 'therapy')?.value as string | undefined
    const dateRange = results.find((r) => r.property === 'date')?.value as [string, string] | undefined

    setTherapyType(nextType)
    setSelectedTherapy(nextTherapy)
    setDateFrom(dateRange?.[0])
    setDateTo(dateRange?.[1])
    setPagination(prev => ({...prev, page: 0}))
  }

  useEffect(() => {
    fetchTherapies()
  }, [])

  useEffect(() => {
    fetchData()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTherapy, pagination, therapyType, dateFrom, dateTo])

  const [file, setFile] = useState<any>()
  const [fileName, setFileName] = useState<any>()
  const [openViewModal, setOpenViewModal] = useState<boolean>(false)
  const [statusDownload, setStatusDownload] = useState<string>()
  const [pendingModalOpen, setPendingModalOpen] = useState<boolean>(false)
  const [isLoading, setIsLoading] = useState<boolean>(false)

  useEffect(() => {
    if (pendingModalOpen && file && statusDownload === STATUS.FULFILLED) {
      setOpenViewModal(true)
      setPendingModalOpen(false)
    }
  }, [file, statusDownload, pendingModalOpen])

  const handleModalView = async (row: any) => {
    setOpenViewModal(true)
    const fileResponse = await apiClient.generatePdf(`${new URL(window.location.href).origin}/${PATHS.PDF_DIARY}?filters=${JSON.stringify({})}`, setStatusDownload)
    const blob = new Blob([new Uint8Array(fileResponse)], {type: 'application/pdf'})
    setFile((window.URL || window.webkitURL).createObjectURL(blob))
    setFileName(`Diario - ${userFirstName} ${userLastName}`)
  }

  const exportDiaryExcel = async () => {
    const response = await apiClient.downloadDiaryExcel(patientId, setStatusDownload)

    const url = window.URL.createObjectURL(response.data)
    const a = document.createElement('a')
    a.href = url
    a.download = 'Diario.xlsx'
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    window.URL.revokeObjectURL(url)
  }

  const onPreviewAssumption = async (assumption: any) => {
    setIsLoading(true)
    const response = await apiClient.exportPatientAssumption(patientId, assumption._id, setStatusDownload)
    setIsLoading(false)

    if (response) {
      const assumptionDate = new Date(assumption.date).toLocaleDateString('it-IT', {day: '2-digit', month: '2-digit', year: 'numeric'}).replace(/\//g, '-')
      const downloadFileName = `Scheda_infusionale_${assumptionDate}.pdf`

      const blob = new Blob([response.data], {type: 'application/pdf'})
      const filepath = URL.createObjectURL(blob)

      const newWindow = window.open(`${filepath}#toolbar=0`, '_blank')

      if (newWindow) {
        newWindow.document.title = downloadFileName
      }
    }
  }

  const onExportAssumption = async (assumption: any) => {
    setIsLoading(true)
    const response = await apiClient.exportPatientAssumption(patientId, assumption._id, setStatusDownload)
    setIsLoading(false)

    if (response) {
      const assumptionDate = new Date(assumption.date).toLocaleDateString('it-IT', {day: '2-digit', month: '2-digit', year: 'numeric'}).replace(/\//g, '-')
      const downloadFileName = `Scheda_infusionale_${assumptionDate}.pdf`

      const blob = new Blob([response.data], {type: 'application/pdf'})
      const url = URL.createObjectURL(blob)

      const link = document.createElement('a')
      link.href = url
      link.download = downloadFileName
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(url)
    }
  }

  // "Terapia" narrows to one specific plan. With a "Tipo" field alongside it, it depends on that
  // field's value (its own list — primary or secondary — and disabled with a hint while Tipo is
  // "Tutte", since psp-backend/diary's merged view doesn't accept a plan id — see getDiary.js's
  // getMergedDiary). Without a "Tipo" field (no accessory therapies, or DIARY_MORISKY, which
  // never gets one) it's just a plain static list of the patient's primary therapies, same as
  // before this filter existed.
  const therapyOptions = (list: Record<string, any>[]) => list.map((therapy: any) => ({
    label: therapy.planName ?? therapy.drug?.name ?? therapy.therapyType?.name ?? therapy._id,
    value: therapy._id
  }))

  // Only shown once there is a second collection to switch to — a PSP where the patient has
  // no accessory therapies keeps exactly today's single-select filter.
  const showTypeField = diaryType !== 'DIARY_MORISKY' && secondaryTherapyOptions.length > 0

  // FilterItem.defaultValue is typed string[] | [number, number] (for multiSelect/range fields)
  // — too narrow for a plain 'select' field's string default, hence the casts below.
  const filtersConfig: FilterItem[] = showTypeField ? [
    {
      id: 'therapyType',
      label: 'Tipo',
      type: 'select',
      defaultValue: therapyType as any,
      // '' (falsy) rather than a sentinel like 'ALL': Filters.onFinish strips any falsy value
      // from the submitted results, so selecting "Tutte" naturally ends up indistinguishable
      // from never touching the field — both correctly omit `therapyType` (Tipo: Tutte).
      options: [
        {label: 'Tutte', value: ''},
        {label: 'Terapia primaria', value: 'PRIMARY'},
        {label: 'Terapia accessoria', value: 'SECONDARY'}
      ]
    },
    {
      id: 'therapy',
      label: 'Terapia',
      type: 'select',
      defaultValue: selectedTherapy as any,
      dependsOn: 'therapyType',
      loadOptions: async (values: Record<string, any>) => therapyOptions(values.therapyType === 'SECONDARY' ? secondaryTherapyOptions : primaryTherapyOptions)
    },
    {id: 'date', label: 'Data', type: 'rangePicker'}
  ] : [
    {
      id: 'therapy',
      label: 'Terapia',
      type: 'select',
      defaultValue: selectedTherapy as any,
      options: therapyOptions(primaryTherapyOptions)
    },
    // DIARY_MORISKY never gets a date filter — psp-backend's getDiaryMorisky doesn't support one.
    ...(diaryType !== 'DIARY_MORISKY' ? [{id: 'date', label: 'Data', type: 'rangePicker'}] : [])
  ]

  // Kept as its own value rather than restructuring the whole render tree: everything below is
  // unchanged from before this tab existed, only now optionally wrapped in a second tab.
  const primaryContent = (
      <TherapyDiaryStyled>
        <div style={{display: 'flex', justifyContent: 'flex-end', gap: '16px', padding: '14px'}}>
          {/* Same "Filtra" button + popover convention as the rest of the legacy stack instead
              of inline controls — see filtersConfig above. */}
          {showTherapiesFilter && (
            <Filters config={filtersConfig} onSubmit={handleFiltersSubmit} />
          )}
          <FileViewerModal
            MIMETypeValue={'application/pdf'}
            fileName={fileName}
            filepath={file}
            handleClose={() => { setOpenViewModal(false); setFile(undefined); setFileName(undefined) }}
            open={openViewModal}
            status={statusDownload}
          />
          {showExport && (
            <PrimaryButton
              onClick={diaryType === 'DIARY_DRUGS' ? exportDiaryExcel : handleModalView}
              startIcon={<Icon path={mdiDownloadCircleOutline} size={1} />}
            >{'Esporta'}
            </PrimaryButton>
          )}
        </div>
        <Snackbar
          anchorOrigin={{vertical: 'top', horizontal: 'right'}}
          onClose={() => { setStatus(undefined); setStatusCount(undefined) }}
          open={status === STATUS.REJECTED || statusCount === STATUS.REJECTED}
        >
          <div>
            <SnackbarAlertCustom
              onClose={() => { setStatus(undefined); setStatusCount(undefined) }}
              severity='error' sx={{width: '100%'}}
            >
              <Typography variant='h5'>
                {'Errore nel recuperare il diario paziente.'}
              </Typography>
            </SnackbarAlertCustom>
          </div>
        </Snackbar>
        <div style={{padding: '25px 25px 40px', border: '1px solid #EEE'}}>
          <Typography variant='h4'>{tableTitle}</Typography>
        </div>
        <TableCustom
          // Preview/download only exist for the primary-therapy calendar (an "infusion sheet"
          // PDF tied to an `assumptions` document) — nothing equivalent was ever built for
          // secondary-assumptions. "Tipo: Tutte" can mix both row types on the same page, so
          // this is gated per-row (via `condition`) on the row's own `type` rather than on the
          // page-level therapyType filter.
          actions={diaryType === 'DIARY_DRUGS' ? [
            {
              id: 'preview',
              label: 'Visualizza',
              icon: mdiEye,
              action: onPreviewAssumption,
              condition: (row: any) => row.type !== 'SECONDARY'
            },
            {
              id: 'download',
              label: 'Download',
              icon: mdiDownload,
              action: onExportAssumption,
              condition: (row: any) => row.type !== 'SECONDARY'
            }
          ] : []}
          columns={[
            // "Stato" always goes last among the data columns (right before Terapia/Azioni),
            // regardless of where the PSP config happens to list it.
            ...columns.filter((col: TableColumn) => col.id !== 'state' && (col.id !== 'adherence' || serviceConf === 'EXTRA')),
            // "Tipo: Tutte" mixes primary and secondary rows on the same page, so each row needs
            // its own label — same gating as the "Tipo" select above (diaryType !== 'DIARY_MORISKY'
            // && secondaryTherapyOptions.length > 0), zero visual difference otherwise.
            ...(diaryType !== 'DIARY_MORISKY' && secondaryTherapyOptions.length > 0 ? [{
              id: 'type',
              label: 'Terapia',
              render: (value: string) => value === 'SECONDARY' ? 'Terapia accessoria' : 'Terapia primaria'
            }] : []),
            ...columns.filter((col: TableColumn) => col.id === 'state')
          ]}
          isLoading={isLoading}
          onPaginationChange={setPagination}
          pagination={pagination}
          rows={data}
          totalElements={count}
        />
      </TherapyDiaryStyled>
  )

  const hasSecondaryTherapies = secondaryTherapyOptions.length > 0

  return (
    <LayoutContainer breadcrumbs={[{label: 'Diario paziente', href: PATHS.AGENDA}]}>
      <TabsLayout
        // `initialValue` is only ever read once, at mount (TabsLayout keeps its own `value`
        // state) — but `hasSecondaryTherapies` is still false on that very first render (the
        // fetch that populates secondaryTherapyOptions hasn't resolved yet), so without this key
        // a deep link wanting "secondary" would always mount with `initialValue: 0` and get stuck
        // on "Terapie" forever. The key forces a fresh mount (and a fresh read of `initialValue`)
        // once the fetch actually resolves.
        key={hasSecondaryTherapies ? 'with-secondary' : 'primary-only'}
        initialValue={location.state?.tab === 'secondary' && hasSecondaryTherapies ? 1 : 0}
        tabs={[
          {label: 'Terapie'},
          // Only added once there is something to show — a patient with no accessory therapies
          // keeps exactly today's single-tab page (zero visual difference).
          ...(hasSecondaryTherapies ? [{label: 'Altre terapie'}] : [])
        ]}
        contents={[
          primaryContent,
          ...(hasSecondaryTherapies ? [
            <SecondaryAssumptionsDiaryPanel
              secondaryTherapies={secondaryTherapyOptions}
              initialTherapyId={location.state?.secondaryTherapyId}
            />
          ] : [])
        ]}
      />
    </LayoutContainer>
  )
}

export default TherapyDiary
